/**
 * useRenderedAspectRatio — PLAT-11407 follow-up to PLAT-11508.
 *
 * Size a preview frame from the composition the renderer is ACTUALLY holding,
 * not from the dimension the UI believes is selected.
 *
 * Why: Creatomate fits a composition into its iframe preserving the
 * composition's own ratio, so any disagreement between the frame's box and the
 * source shows up as bars around the video. (The 125% iframe oversize in
 * interactive mode is not a crop — it cancels Creatomate's own 80% inset; see
 * INTERACTIVE_IFRAME_FIT.)
 *
 * The durable fix is to stop sizing the box from `selectedDimension` — a label
 * that can drift from the loaded source — and size it from the source itself.
 * Then the frame fills its box at every ratio, and a drift can never present
 * as a rendering defect again (it surfaces on the dimension chip instead,
 * where it can be diagnosed).
 *
 * Returns a CSS `aspect-ratio` value. Raw `width / height` is used verbatim —
 * no fraction reduction — so an off-catalog composition (1084x1920) is exact
 * rather than snapped to a named ratio.
 */

import type { VideoSourceStore } from '@src/stores-v2/VideoSourceStore';

/**
 * Normalize a dimension label to a CSS `aspect-ratio` value: `'16:9'` becomes
 * `'16 / 9'`. A value that is already in CSS form passes through, and an
 * absent one yields `undefined` so a styled component can fall back.
 */
export function cssAspectRatio(
  value: string | null | undefined,
): string | undefined {
  if (!value) return undefined;
  return value.includes(':') ? value.replace(':', ' / ') : value;
}

/**
 * The CSS `aspect-ratio` for the composition currently loaded in
 * `videoSourceStore`, falling back to `fallbackDimension` until the source has
 * usable dimensions (first paint, or a store that never loaded).
 *
 * Not memoized: it reads two numbers off an `observable.ref` and formats a
 * string, and a `useMemo` would only add a dependency array to keep correct.
 * Callers are `observer` components, so reading `source` here subscribes them
 * to a source swap.
 */
export function useRenderedAspectRatio(
  videoSourceStore: VideoSourceStore | null | undefined,
  fallbackDimension: string | null | undefined,
): string | undefined {
  const source = videoSourceStore?.source;
  const width = Number(source?.width);
  const height = Number(source?.height);
  // A partially-loaded source can carry a 0 or a NaN; neither is a ratio.
  if (
    Number.isFinite(width) &&
    Number.isFinite(height) &&
    width > 0 &&
    height > 0
  ) {
    return `${width} / ${height}`;
  }
  return cssAspectRatio(fallbackDimension);
}
